import type {
  OutboundTaskAggregateWire,
  OutboundTaskRootStatusWire,
} from '../../lib/types';

/**
 * F4-b — adaptadores PUROS da superfície de campanha (Raiz de Tarefa). Traduzem
 * o contrato genérico da F4-a em rótulos pt-BR NEUTROS (nenhum literal setorial;
 * o "significado" de cada slot vive no playbook do tenant). Testável isolado.
 */

/** Rótulo pt-BR de cada estado da campanha (neutro). */
export const CAMPAIGN_STATUS_LABEL: Record<OutboundTaskRootStatusWire, string> = {
  draft: 'Rascunho',
  dispatching: 'Disparando',
  active: 'Em andamento',
  awaiting_review: 'Aguardando revisão',
  closed: 'Encerrada',
  cancelled: 'Cancelada',
};

/** Classe de badge DS por estado (semântica de status, não de marca). */
export const CAMPAIGN_STATUS_BADGE: Record<OutboundTaskRootStatusWire, string> = {
  draft: 'badge--neutral',
  dispatching: 'badge--info',
  active: 'badge--brand',
  awaiting_review: 'badge--warning',
  closed: 'badge--neutral',
  cancelled: 'badge--neutral',
};

/** Motivos de skip do dispatch (spec §4.2) → rótulo pt-BR neutro. */
const SKIP_REASON_LABEL: Record<string, string> = {
  'feature-disabled': 'Recurso desativado',
  'capability-revoked': 'Permissão revogada',
  'no-playbook': 'Sem playbook',
  'playbook-inactive': 'Playbook inativo',
  'lgpd-opposition': 'Oposição LGPD',
  'adversarial-blocked': 'Bloqueado por conflito',
  'communication-opt-out': 'Opt-out de comunicação',
  'no-identity': 'Sem identidade resolvível',
  'active-conversation-exists': 'Já em conversa ativa',
  'template-missing': 'Sem template (HSM)',
  'opening-missing': 'Sem abertura',
  'no-owner-for-pendency': 'Sem responsável (H_a)',
};

export function skipReasonLabel(key: string): string {
  return SKIP_REASON_LABEL[key] ?? key;
}

/** Status de despacho da SOA-filha → rótulo. */
const DISPATCH_STATUS_LABEL: Record<string, string> = {
  scheduled: 'Agendadas',
  dispatched: 'Disparadas',
  skipped: 'Puladas',
  deferred: 'Adiadas',
  cancelled: 'Canceladas',
};

export function dispatchStatusLabel(key: string): string {
  return DISPATCH_STATUS_LABEL[key] ?? key;
}

/** Desfecho-proxy de cada conversa-filha → rótulo pt-BR neutro. */
export const OUTCOME_LABEL: Record<string, string> = {
  'in-progress': 'Em andamento',
  'auto-closed': 'Encerradas pela IA',
  escalated: 'Escaladas',
  'routed-to-resolution': 'Roteadas para resolução',
  unknown: 'Indefinido',
};

export function outcomeLabel(key: string): string {
  return OUTCOME_LABEL[key] ?? key;
}

/**
 * Segmentos da barra de progresso (settled / em-andamento / pendente). Deriva do
 * agregado sem inventar: em-andamento = disparadas com conversa não-terminal,
 * aproximado por outcome `in-progress`; o resto pendente = total − settled − andamento.
 * Percentuais somam 100 (guard total=0 → tudo pendente 0%).
 */
export function progressSegments(agg: OutboundTaskAggregateWire): {
  settled: number;
  inProgress: number;
  pending: number;
  settledPct: number;
  inProgressPct: number;
  pendingPct: number;
} {
  const total = agg.progress.total;
  const settled = agg.progress.settled;
  const inProgress = agg.outcome.byState['in-progress'] ?? 0;
  // pending = tudo que não assentou e não está em andamento contabilizado.
  const pending = Math.max(0, agg.progress.pending - inProgress);
  const pct = (n: number): number => (total > 0 ? Math.round((n / total) * 100) : 0);
  return {
    settled,
    inProgress,
    pending,
    settledPct: pct(settled),
    inProgressPct: pct(inProgress),
    pendingPct: pct(pending),
  };
}

/** Entradas de skip não-zero, ordenadas por contagem desc (para exibir só o que importa). */
export function nonZeroEntries(rec: Record<string, number>): [string, number][] {
  return Object.entries(rec)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);
}
